import {
  getRESTParams,
  postRESTParams,
  REMBLE_API_URL
  // TYPE_COURSE
} from '.'

const BASE_PATH = `${REMBLE_API_URL}/users`
let _userPreferenceOptions = null

/* =============================================
 * Private helpers
 * ============================================= */

/**
 * Common Users GET request pattern.
 * @param {String} url The REST URL to query.
 * @param {Object} params Parameter object to merge with common params, if necessary.
 * See getRESTParams method for more info.
 */
const _usersRESTGET = async (url, params) => {
  const result = await fetch(url, getRESTParams(params))
    .then(response => response.json())
    .catch(error => {
      console.error('error', error)
      return { error }
    })

  return result
}

/**
 * Common Users POST request pattern.
 * @param {String} url The REST URL to post to.
 * @param {Object} params Parameter object to merge with common params, if necessary.
 * See postRESTParams method for more info.
 */
const _usersRESTPOST = async (url, params) =>
  _usersRESTGET(
    url,
    postRESTParams(params, { 'Content-Type': 'application/json' })
  )

/* =============================================
 * Public methods
 * ============================================= */

/**
 * Makes an API call to the /users/login endpoint to authenticate the user
 * @param {Object} user a JSON object that contains `email` and `password` fields.
 */
const authenticateUser = async user => {
  const url = `${BASE_PATH}/login`
  const body = JSON.stringify({ ...user })
  const isAuthenticated = await _usersRESTPOST(url, { body }).catch(error => {
    console.error('error', error)
    return { error }
  })

  return isAuthenticated
}

/**
 * Handles new account signup for a user given a name, email, password, and password confirmation
 * @param {Object} user a JSON object that contains `name`, `email`, `password`, and `password_confirm` fields.
 */
const createUser = async user => {
  const url = `${BASE_PATH}/create`
  const body = JSON.stringify({ ...user })
  return _usersRESTPOST(url, { body })
}

/**
 * Makes an API call to the /users/verify_resend endpoint to resend email verification
 * @param {String} email the cognito email for which to resend the verification code
 * @return Returns a boolean indicating whether the email was sent successfully
 */
const sendNewVerificationCode = async email => {
  // Return early if no email is provided
  if (!email) return false

  const url = `${BASE_PATH}/verify_resend`
  const body = JSON.stringify({ email })
  const isVerified = await _usersRESTPOST(url, { body }).catch(error => {
    console.error('error', error)
    return { error }
  })
  return isVerified && isVerified.success
}

/**
 * Makes an API call to the /users/forgot_password endpoint to reset user's password
 * @param {String} email the cognito email/username for which to send the reset code
 */
const sendForgotPasswordCode = async email => {
  const url = `${BASE_PATH}/forgot_password`
  const body = JSON.stringify({ email })
  const result = await _usersRESTPOST(url, { body }).catch(error => {
    console.error('error', error)
    return { error }
  })
  return result
}

/**
 * Makes an API call to the /users/forgot_password/confirm endpoint to reset the user's password
 * @param {String} email the cognito email/username for which to send the reset code
 * @param {String} code the reset code sent to the user
 * @param {String} password the new password
 */
const confirmForgotPasswordCode = async (email, code, password) => {
  const url = `${BASE_PATH}/forgot_password/confirm`
  const body = JSON.stringify({ email, code, password })
  const result = await _usersRESTPOST(url, { body }).catch(error => {
    console.error('error', error)
    return { error }
  })
  return result
}

/**
 * Makes an API call to the /users/verify endpoint to confirm that the username/email is unique
 * @param {String} email The username/email to check for uniqueness
 * @returns Returns an object with an error or an object with a "valid" property set to true or false
 */
const verifyUsernameIsUnique = async email => {
  const url = `${BASE_PATH}/verify_unique`
  const body = JSON.stringify({ email })
  const isUnique = await _usersRESTPOST(url, { body }).catch(error => {
    console.error('error', error)
    return { error }
  })

  return isUnique
}

/**
 * Makes an API call to the /users/verify endpoint to complete email verification
 * @param {Object} verification a JSON object that contains email and code fields.
 */
const verifyUser = async verification => {
  const url = `${BASE_PATH}/verify`
  const body = JSON.stringify({ ...verification })
  const isVerified = await _usersRESTPOST(url, { body }).catch(error => {
    console.error('error', error)
    return { error }
  })

  return isVerified
}

/**
 * Makes an API call to the /users/tos-acceptance endpoint to record the user's acceptance of the TOS
 */
const updateTOSAcceptance = async () => {
  const url = `${BASE_PATH}/tos-acceptance`
  const body = JSON.stringify({ terms_accepted: true })
  const success = await _usersRESTPOST(url, { body }).catch(error => {
    console.error('error', error)
    return { error }
  })

  return success
}

/**
 * Makes an API call to the /users/tos-acceptance/ai endpoint to get the user's acceptance of the AI TOS
 */
const getAITOSAcceptance = async () => {
  const url = `${BASE_PATH}/tos-acceptance/ai`
  const success = await _usersRESTGET(url).catch(error => {
    console.error('error', error)
    return { error }
  })

  return success
}

/**
 * Makes an API call to the /users/tos-acceptance/ai endpoint to record the user's acceptance of the AI TOS
 * @param {Boolean} terms_accepted Whether the user has accepted the AI TOS
 */
const updateAITOSAcceptance = async (terms_accepted = true) => {
  const url = `${BASE_PATH}/tos-acceptance/ai`
  const body = JSON.stringify({ terms_accepted })
  const success = await _usersRESTPOST(url, { body }).catch(error => {
    console.error('error', error)
    return { error }
  })

  return success
}

/**
 * Returns an object containing options for focus areas, practices, topics, and notifications
 * @returns An object with all possible options for focus areas, practices, topics, and notifications
 */
const getUserPreferenceOptions = async () => {
  // If we already have the options stored in the private variable, return them
  if (_userPreferenceOptions) return _userPreferenceOptions

  const url = `${BASE_PATH}/preferences/options`
  const result = await _usersRESTGET(url).catch(error => {
    console.error('error', error)
    return { error }
  })

  const options = {
    areas: result.focus_areas,
    practices: result.focus_practices,
    topics: result.focus_topics,
    notifications: result.notifications
  }

  // Store the options in a private variable so we don't have to make another API call
  _userPreferenceOptions = options

  return options
}

/**
 * Makes an API call to the /users/preferences endpoint to retrieve user preferences. Returns { focus_areas, focus_practices }
 */
const getUserPreferences = async () => {
  const url = `${BASE_PATH}/preferences`
  const result = await _usersRESTGET(url).catch(error => {
    console.error('error', error)
    return { error }
  })
  return result
}

/**
 * Makes an API call to the /users/preferences endpoint to save user preferences. Expects { focus_areas, focus_practices }
 * @param {Object} preferences the user's preferences as a JSON object
 */
const saveUserPreferences = async preferences => {
  const url = `${BASE_PATH}/preferences`
  const body = JSON.stringify(preferences)
  const result = await _usersRESTPOST(url, { body }).catch(error => {
    console.error('error', error)
    return { error }
  })
  return result
}

/**
 * Returns an object containing the user assessment form data
 * @param {Number} version the version number of the assessment form to retrieve
 * @returns An object that describes the user assessment form
 */
const getUserAssessmentFormData = async version => {
  let url = `${BASE_PATH}/assessments/form`

  // If a version is provided, add it to the query string
  if (version) url += `?version=${version}`

  const result = await _usersRESTGET(url).catch(error => {
    console.error('error', error)
    return { error }
  })

  return result
}

/**
 * Returns an object containing the most recent user assessment data
 * @returns An object that describes the user assessment data
 */
const getSavedUserAssessmentData = async () => {
  const url = `${BASE_PATH}/assessments/`
  const result = await _usersRESTGET(url).catch(error => {
    console.error('error', error)
    return { error }
  })

  return result
}

/**
 * Returns an object containing a plan for the user based on their assessment data
 * @param {Number} version the version number of the assessment plan to retrieve. Defaults to 2.
 * @returns An array that contains all of the user programs
 */
const getSavedUserAssessmentPlan = async (version = 2) => {
  let programs = []

  try {
    const url = `${BASE_PATH}/assessments/plan?version=${version}`
    programs = await _usersRESTGET(url).catch(error => {
      console.error('error', error)

      // If the error is a 404, it means the user hasn't taken the assessment yet. Let's return an empty array.
      if (error.status === 404) return []
    })

    // If programs has a detail property, it's an error. This may just mean that the user hasn't taken the assessment yet.
    // Let's log the error and return an empty array.
    if (programs.detail) {
      console.error('error', programs.detail)
      programs = []
    }
  } catch (error) {
    console.error('error', error)
  }

  return programs
}

/**
 * Saves the user assessment answer data
 * @param {Object} answerData the user's assessment answer data
 * @returns {Object} the result of the API call
 */
const saveUserAssessmentData = async answerData => {
  const url = `${BASE_PATH}/assessments/form`
  const body = JSON.stringify({ assessment: answerData })
  const result = await _usersRESTPOST(url, { body }).catch(error => {
    console.error('error', error)
    return { error }
  })
  return result
}

/**
 * Saves the user's rating on a search result
 * @param {Object} ratingData the user's rating data
 * @param {String} ratingData.id the ID of the search result
 * @param {Number} ratingData.rating the user's rating of the search result
 * @param {String} ratingData.feedback the user's feedback on the search result
 * @returns {Object} the result of the API call
 */
const saveUserRating = async ({ id, rating, feedback }) => {
  const url = `${BASE_PATH}/search/rate`

  let userRating = { id }

  // Only include the rating if we have one
  if (rating) userRating.rating = rating

  // Only include the feedback if we have some
  if (feedback) userRating.feedback = feedback

  const body = JSON.stringify(userRating)
  const result = await _usersRESTPOST(url, { body }).catch(error => {
    console.error('error', error)
    return { error }
  })
  return result
}

export {
  authenticateUser,
  confirmForgotPasswordCode,
  verifyUsernameIsUnique,
  getUserPreferences,
  getUserPreferenceOptions,
  saveUserPreferences,
  sendNewVerificationCode,
  sendForgotPasswordCode,
  createUser,
  verifyUser,
  updateTOSAcceptance,
  getUserAssessmentFormData,
  saveUserAssessmentData,
  getSavedUserAssessmentData,
  getSavedUserAssessmentPlan,
  saveUserRating,
  getAITOSAcceptance,
  updateAITOSAcceptance
}
